import { Link } from '@tanstack/react-router';
import type { Permission } from '@maestro/shared/permissions';
import { useHasPermission } from '@/lib/permissions';

// The console's pages, in the order an operator moves through them: the board first,
// then the read-only views over what it did. A page carrying a `permission` is only
// listed for a role that holds it, so the strip never offers a link that would redirect.
interface NavPageProps {
  to: string;
  label: string;
  permission?: Permission;
}

const PAGES: readonly NavPageProps[] = [
  { to: '/console', label: 'Board' },
  { to: '/live', label: 'Live' },
  { to: '/responsiveness', label: 'Responsiveness' },
  { to: '/pool-time', label: 'Pool time' },
  { to: '/call-history', label: 'Call history', permission: 'call-history:read' },
];

// Page navigation, deliberately NOT rendered as buttons: everything else in these
// headers is an action or a settings modal, and an operator has no way to tell the
// two apart when they share a shape. An underlined strip with the current page
// marked reads as "where I am", not "what happens if I click".
export function ConsoleNav() {
  return (
    <nav className="flex items-center gap-1">
      {PAGES.map((p) => (
        <NavPage key={p.to} {...p} />
      ))}
    </nav>
  );
}

// Its own component because the permission check is a hook, and a hook cannot be called
// from inside a map callback.
function NavPage({ to, label, permission }: NavPageProps) {
  // Only a page that names a permission is conditional; the rest are already behind the
  // console's own gate.
  const permitted = useHasPermission(permission ?? 'console:view');
  if (permission && !permitted) return null;

  return (
    <Link
      to={to}
      // Styled off the router's own data-status rather than activeProps: those
      // class strings are concatenated, not merged, so the active and inactive
      // colours would both land on the element and fight over stylesheet order.
      className="text-muted-foreground hover:text-foreground data-[status=active]:border-foreground data-[status=active]:text-foreground border-b-2 border-transparent px-2 pb-1 text-sm font-medium"
    >
      {label}
    </Link>
  );
}
